import { BackButton } from 'components/Mobile/Mobile.styles'
import { cn } from 'components/UI/cn'
import { ChevronLeft } from 'lucide-react'
import { useEffect } from 'react'
import { PiXCircle } from 'react-icons/pi'
import { createPortal } from 'react-dom'
import { Z_INDEX } from 'styles/z-index'

interface Props {
  header: React.ReactNode
  footer: React.ReactNode
  children: React.ReactNode
  handleBack?: () => void
  backLabel?: string
  backIcon?: React.ReactNode
  /**
   * Cover the whole viewport — including the top bar and bottom nav — instead of
   * the band between them. Opt-in: existing callers keep the inset layout. Used
   * by focused, single-task flows (e.g. the contact-create wizard) that want the
   * maximum space and rely on their own close affordance to exit. Header/footer
   * gain safe-area insets so content clears the notch and home indicator.
   */
  fullScreen?: boolean
  /**
   * When set (with `fullScreen`), renders a full-width green "Cancel" bar at the
   * very top — the focused-flow exit affordance, replacing a corner close icon.
   * The bar absorbs the top safe-area inset (so it covers the notch) and frees
   * the header below for the stepper / title.
   */
  onCancel?: () => void
}

export function MobileModal({
  header,
  footer,
  children,
  handleBack,
  backLabel = 'Back',
  backIcon,
  fullScreen = false,
  onCancel,
}: Props) {
  const showCancelBar = fullScreen && !!onCancel
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  // Layout (FIR-7449): the modal occupies the band between the mobile top bar
  // (51px) and the bottom nav (60px) as a flex column with three regions:
  // a non-shrinking header, a single internally-scrolling body, and a pinned
  // footer. The previous implementation scrolled the whole container, let the
  // header scroll away, positioned the footer with `fixed bottom-[60px]` (so it
  // floated over the body and needed a magic `paddingBottom: 220` to compensate),
  // and ran 51px past the viewport bottom — which on mobile produced the
  // inconsistent / locked scrolling reported in the ticket. `overscroll-contain`
  // stops touch-scroll from chaining to the page behind the modal.
  //
  // fullScreen (FIR-7725): cover the whole viewport (over both navs). It needs a
  // z-index above MOBILE_FOOTER (1000) to sit over the bottom nav, and safe-area
  // insets on the header/footer so they clear the notch / home indicator.
  return createPortal(
    <div
      className={cn(
        'fixed inset-x-0 bg-white flex flex-col',
        fullScreen
          ? 'inset-0'
          : 'top-[48px] bottom-[calc(60px+env(safe-area-inset-bottom))] z-50'
      )}
      style={
        fullScreen ? { zIndex: Z_INDEX.MOBILE_FULLSCREEN_MODAL } : undefined
      }
    >
      {showCancelBar && (
        <div className="w-full flex-shrink-0 bg-[var(--nav-bg-hover)] p-3 pt-[calc(env(safe-area-inset-top)+15px)] shadow-[0px_6px_29px_rgba(196,203,214,0.1)]">
          <button
            type="button"
            onClick={onCancel}
            className="flex items-center gap-1 text-white"
          >
            <PiXCircle className="size-6" />
            <span className="text-md">Cancel</span>
          </button>
        </div>
      )}
      {handleBack && (
        <BackButton onClick={handleBack}>
          {backIcon ?? <ChevronLeft size={20} />}
          <span>{backLabel}</span>
        </BackButton>
      )}
      <div
        className={cn(
          'flex-shrink-0 p-2 bg-gray-50 flex flex-col border',
          // The green Cancel bar already absorbs the top safe-area inset; only
          // pad the header for it when there's no bar.
          fullScreen &&
            !showCancelBar &&
            'pt-[calc(env(safe-area-inset-top)+0.5rem)]'
        )}
      >
        {header}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2">
        {children}
      </div>
      <div
        className={cn(
          'flex-shrink-0 border-t bg-gray-50 p-2 w-full',
          fullScreen && 'pb-[calc(env(safe-area-inset-bottom)+0.5rem)]'
        )}
      >
        {footer}
      </div>
    </div>,
    document.body
  )
}
